<template>
  <EditorShell
    ref="shell"
    :items="shellItems"
    item-label="terrain"
    @remove="onShellRemove"
  >
    <template #add-panel>
      <div v-if="terrainsLoading" class="field-desc">Loading terrain skins…</div>
      <div v-else-if="terrainsError" class="field-error">{{ terrainsError }}</div>
      <SkinGrid
        v-else
        :skins="terrainSkins"
        :selection-mode="true"
        :selected-ids="selectedTerrainIds"
        :show-info="false"
        :card-size="skinSize"
        @toggle="toggleTerrain"
      />
    </template>

    <template #edit-panel="{ item, idx, openLightbox }">
      <div class="ve-edit-form">
        <div class="ve-edit-row-head">
          <img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            class="ve-edit-thumb"
            :style="{ width: skinSize + 'px', height: skinSize + 'px' }"
            @click="openLightbox(item.thumbnailUrl)"
          />
          <div>
            <div class="ve-edit-skin-name">{{ terrainSkinById(item.id).name }}</div>
            <div class="ve-edit-hint">Terrain {{ idx + 1 }} of {{ selectedTerrainIds.length }}</div>
          </div>
        </div>
        <div class="ve-edit-group">
          <label class="ve-edit-label">Name</label>
          <input
            :value="(terrainDetails[item.id] || {}).name"
            type="text"
            class="field-input"
            @input="onFieldInput(item.id, 'name', $event.target.value)"
          />
        </div>
        <div class="ve-edit-group">
          <label class="ve-edit-label">Width (in)</label>
          <input
            :value="(terrainDetails[item.id] || {}).widthIn"
            type="number" min="0" step="0.5"
            class="field-input"
            @input="onFieldInput(item.id, 'widthIn', parseFloat($event.target.value) || 0)"
          />
        </div>
        <div class="ve-edit-group">
          <label class="ve-edit-label">Length (in)</label>
          <input
            :value="(terrainDetails[item.id] || {}).lengthIn"
            type="number" min="0" step="0.5"
            class="field-input"
            @input="onFieldInput(item.id, 'lengthIn', parseFloat($event.target.value) || 0)"
          />
        </div>
        <div class="ve-edit-group">
          <label class="ve-edit-label">Shape</label>
          <div class="terrain-shape-toggle" style="max-width:200px">
            <button
              type="button"
              class="terrain-shape-btn"
              :class="{ active: (terrainDetails[item.id] || {}).shape === 0 }"
              @click="onFieldInput(item.id, 'shape', 0)"
            >Square</button>
            <button
              type="button"
              class="terrain-shape-btn"
              :class="{ active: (terrainDetails[item.id] || {}).shape === 1 }"
              @click="onFieldInput(item.id, 'shape', 1)"
            >Round</button>
          </div>
        </div>
      </div>
    </template>
  </EditorShell>
</template>

<script>
import EditorShell from '@/components/EditorShell.vue';
import SkinGrid from '@/components/SkinGrid.vue';

const IN_TO_CM = 2.54;

export default {
  name: 'TerrainEditor',
  components: { EditorShell, SkinGrid },

  props: {
    value: { type: Array, default: () => [] },
    skinSize: { type: Number, default: 64 },
  },

  data() {
    return {
      terrainSkins: [],
      terrainsLoading: false,
      terrainsError: '',
      selectedTerrainIds: [],
      terrainDetails: {},
    };
  },

  computed: {
    shellItems() {
      return this.selectedTerrainIds.map(skinId => {
        const skin = this.terrainSkinById(skinId);
        return {
          id: skinId,
          thumbnailUrl: skin.thumbnailUrl || '',
          name: (this.terrainDetails[skinId] || {}).name || skin.name || skinId,
        };
      });
    },
  },

  async mounted() {
    await this.fetchTerrainSkins();
  },

  watch: {
    value(newVal) {
      if (this.terrainSkins.length > 0) {
        this.initFromValue(newVal);
      }
    },
  },

  methods: {
    async fetchTerrainSkins() {
      this.terrainsLoading = true;
      this.terrainsError = '';
      try {
        const res = await fetch(
          '/GetCatalogueSkins?viewMode=public&search=terrain&perPage=200&page=1&sortOrder=newest',
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        this.terrainSkins = data.result.skins;
        if (this.value && this.value.length > 0) {
          this.initFromValue(this.value);
        }
      } catch (err) {
        this.terrainsError = 'Could not load terrain skins: ' + err.message;
      } finally {
        this.terrainsLoading = false;
      }
    },

    initFromValue(terrains) {
      const newSelectedIds = [];
      for (const terrain of (terrains || [])) {
        let skinId = null;
        if (terrain.SkinId != null) {
          // New format: explicit SkinId field
          const skin = this.terrainSkins.find(s => String(s.id) === String(terrain.SkinId));
          skinId = skin ? String(skin.id) : String(terrain.SkinId);
        } else if (terrain.Id != null && !/^terrain_/.test(String(terrain.Id))) {
          // Old format: Id is the numeric skin ID (e.g. "6585"), not "terrain_0" style
          const skin = this.terrainSkins.find(s => String(s.id) === String(terrain.Id));
          skinId = skin ? String(skin.id) : String(terrain.Id);
        } else {
          // Last resort: match by exact name
          const skin = this.terrainSkins.find(s => s.name === terrain.Name);
          if (skin) skinId = String(skin.id);
        }
        if (skinId && !newSelectedIds.includes(skinId)) {
          newSelectedIds.push(skinId);
          this.$set(this.terrainDetails, skinId, {
            name: terrain.Name,
            widthIn: terrain.Width != null ? Math.round((terrain.Width / IN_TO_CM) * 100) / 100 : 4,
            lengthIn: terrain.Length != null ? Math.round((terrain.Length / IN_TO_CM) * 100) / 100 : 4,
            shape: terrain.Shape ?? 0,
          });
        }
      }
      this.selectedTerrainIds = newSelectedIds;
    },

    terrainSkinById(skinId) {
      return this.terrainSkins.find(s => String(s.id) === skinId) || {};
    },

    toggleTerrain(skin) {
      const id = String(skin.id);
      if (this.selectedTerrainIds.includes(id)) return;
      this.selectedTerrainIds.push(id);
      if (!this.terrainDetails[id]) {
        this.$set(this.terrainDetails, id, { name: skin.name, widthIn: 4, lengthIn: 4, shape: 0 });
      }
      this.$refs.shell.selectIdx(this.selectedTerrainIds.length - 1);
      this.$emit('input', this.buildTerrainJson());
    },

    onShellRemove(item, idx) {
      this.selectedTerrainIds.splice(idx, 1);
      this.$emit('input', this.buildTerrainJson());
    },

    onFieldInput(skinId, field, value) {
      this.$set(this.terrainDetails, skinId, { ...this.terrainDetails[skinId], [field]: value });
      this.$emit('input', this.buildTerrainJson());
    },

    buildTerrainJson() {
      return this.selectedTerrainIds.map((skinId, i) => {
        const skin = this.terrainSkinById(skinId);
        const d = this.terrainDetails[skinId] || {};
        return {
          Id: `terrain_${i}`,
          SkinId: skin.id != null ? skin.id : (parseInt(skinId, 10) || skinId),
          Name: d.name || skin.name || skinId,
          Type: 'Terrain',
          Width: Math.round(((d.widthIn ?? 4) * IN_TO_CM) * 100) / 100,
          Length: Math.round(((d.lengthIn ?? 4) * IN_TO_CM) * 100) / 100,
          Shape: d.shape ?? 0,
        };
      });
    },
  },
};
</script>

<style>
.terrain-shape-toggle { display: flex; border: 1px solid #30363d; border-radius: 6px; overflow: hidden; margin-top: 0.1rem; }
.terrain-shape-btn { flex: 1; padding: 0.3rem 0.75rem; background: #0d1117; color: #8b949e; border: none; cursor: pointer; font-size: 0.82rem; transition: background 0.15s, color 0.15s; }
.terrain-shape-btn + .terrain-shape-btn { border-left: 1px solid #30363d; }
.terrain-shape-btn.active { background: #1f6feb; color: #fff; font-weight: 600; }
</style>
